import type { StorefrontProductDetail, StorefrontProductSummary } from '@erp/shared';
import { Heart } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { publicApi } from '../../lib/publicApi.js';
import { useWishlistStore } from '../../lib/wishlistStore.js';

import { useBuyerPricing } from './buyerPricing.js';
import { GroupPricingBadge } from './GroupPricingBadge.js';
import { ProductCard } from './ProductCard.js';
import { HomeSkeleton } from './StorefrontSkeletons.js';

const toSummary = (d: StorefrontProductDetail): StorefrontProductSummary => ({
  id: d.id, slug: d.slug, name: d.name, description: null,
  price: d.price, originalPrice: d.originalPrice, discountPercent: d.discountPercent,
  currencyCode: d.currencyCode,
  imageUrl: d.images[0] ?? null, inStock: d.inStock, lowStock: false, featured: false, isNew: false,
});

/** Storefront wishlist — the device-local saved products (guest-friendly). */
export function StorefrontWishlistPage(): JSX.Element {
  const { t } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const ids = useWishlistStore((s) => (s.slug === slug ? s.items : []));
  const remove = useWishlistStore((s) => s.remove);
  const [products, setProducts] = useState<StorefrontProductSummary[] | null>(null);
  // A shopper on a customer group priced in another currency gets a 404 for any
  // product their group has no price for — that must NOT delete the save.
  const { priceListId, authoritative } = useBuyerPricing();
  const forgetOn404 = authoritative && !priceListId;

  // Re-fetch whenever the saved id set changes. Drops items that 404 (a product
  // that was unpublished / deleted) from both the view and the saved list.
  const key = ids.join(',');
  useEffect(() => {
    if (ids.length === 0) { setProducts([]); return; }
    let cancelled = false;
    void Promise.all(
      ids.map((id) =>
        publicApi().get<StorefrontProductDetail>(`/${slug}/products/${id}`)
          .then((r) => toSummary(r.data))
          .catch(() => { if (forgetOn404) remove(id); return null; }),
      ),
    ).then((rows) => { if (!cancelled) setProducts(rows.filter((r): r is StorefrontProductSummary => r !== null)); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, key, forgetOn404]);

  // Feature is off for this store — don't expose the page by direct URL.
  if (!site.wishlistEnabled) return <Navigate to={`/s/${slug}`} replace />;

  return (
    <div className="flex flex-col gap-6">
      <Helmet><title>{t('store.wishlist.title', { defaultValue: 'Wishlist' })}</title></Helmet>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Heart className="h-6 w-6 text-danger" aria-hidden />
          {t('store.wishlist.title', { defaultValue: 'Wishlist' })}
        </h1>
        <GroupPricingBadge />
      </div>

      {products === null ? (
        <HomeSkeleton />
      ) : products.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-bg-subtle py-16 text-center">
          <Heart className="h-10 w-10 text-fg-subtle" aria-hidden />
          <p className="text-fg-muted">{t('store.wishlist.empty', { defaultValue: 'Your wishlist is empty. Tap the heart on a product to save it.' })}</p>
          <Link to={`/s/${slug}/shop`} className="rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-brand-contrast transition-[transform,opacity] duration-150 ease-out hover:opacity-90 active:scale-[0.97]">
            {t('store.browse', { defaultValue: 'Browse products' })}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
